import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Merges class names, letting a later Tailwind utility win over an earlier one. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Two-letter initials for avatars — handles Greek names and single-word companies. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Two-letter mark for an organisation — the first two letters of the *first* word.
 *
 * Deliberately not `initials()`. A person is identified by first + last initial, but an
 * insurer's distinguishing word is the first one and the rest is boilerplate: taking one
 * letter from each end collapses "Εθνική Ασφαλιστική" and "ERGO Ασφαλιστική" onto the
 * same ΕΑ, while the first two letters keep them apart as ΕΘ and ER. It also lands on
 * the marks people already use for these companies — IN, AL, ER, NN, EU.
 */
export function companyInitials(name: string): string {
  const first = name.trim().split(/\s+/).filter(Boolean)[0];
  if (!first) return '?';
  return first.slice(0, 2).toUpperCase();
}

/** Stable index into a fixed palette, so the same name always gets the same colour. */
export function hashIndex(value: string, buckets: number): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % buckets;
}

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

/** Debounces a value — used by search inputs so each keystroke is not a request. */
export function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number) {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: A) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}
